import type {
  SiteBuilderBlock,
  SiteBuilderContentJson,
} from "@webdo/site-templates/builder";
import {
  builderBlockAtAddress,
  builderBlockEntries,
  type BuilderBlockAddress,
} from "./tree";

export const BUILDER_STUDIO_FORM_CONTROL_BLOCK_TYPES = [
  "field",
  "submitButton",
] as const;

export function isBuilderStudioFormControlBlockType(
  type: string,
): type is typeof BUILDER_STUDIO_FORM_CONTROL_BLOCK_TYPES[number] {
  return (
    BUILDER_STUDIO_FORM_CONTROL_BLOCK_TYPES as readonly string[]
  ).includes(type);
}

/**
 * Returns only authored Field blocks owned directly by this Form. Fields in a
 * nested layout or another nested Form belong to that nested context and must
 * not be managed accidentally from the current Form inspector.
 */
export function builderStudioFormFields(
  form: SiteBuilderBlock,
): readonly SiteBuilderBlock[] {
  if (form.type !== "form") {
    return [];
  }

  return (form.slots?.items ?? []).filter((item) => item.type === "field");
}

/**
 * Adds one already materialized Field to a Form while keeping the submit
 * action last. Invalid blocks and duplicate identities are safe no-ops.
 */
export function withBuilderStudioFormField(
  form: SiteBuilderBlock,
  field: SiteBuilderBlock,
): SiteBuilderBlock {
  if (form.type !== "form" || field.type !== "field") {
    return form;
  }

  const items = form.slots?.items ?? [];
  const fieldUuid = field.uuid?.trim();

  if (
    fieldUuid
    && items.some((item) => item.uuid?.trim() === fieldUuid)
  ) {
    return form;
  }

  const submitIndex = items.findIndex(
    (item) => item.type === "submitButton",
  );
  const insertIndex = submitIndex >= 0 ? submitIndex : items.length;

  return {
    ...form,
    slots: {
      ...form.slots,
      items: [
        ...items.slice(0, insertIndex),
        field,
        ...items.slice(insertIndex),
      ],
    },
  };
}

/**
 * Removes one matching direct Field without touching nested fields or another
 * kind of block that happens to carry the same legacy UUID.
 */
export function withoutBuilderStudioFormField(
  form: SiteBuilderBlock,
  fieldUuid: string,
): SiteBuilderBlock {
  if (form.type !== "form" || fieldUuid.trim() === "") {
    return form;
  }

  const items = form.slots?.items ?? [];
  const fieldIndex = items.findIndex((item) => (
    item.type === "field" && item.uuid === fieldUuid
  ));

  if (fieldIndex < 0) {
    return form;
  }

  return {
    ...form,
    slots: {
      ...form.slots,
      items: [
        ...items.slice(0, fieldIndex),
        ...items.slice(fieldIndex + 1),
      ],
    },
  };
}

/**
 * Resolves the Form that owns the selected block. A newly inserted Form is
 * wrapped in one movable text Column, so the wrapper also deliberately opens
 * the nested Form context. Broader containers never select an arbitrary Form.
 */
export function builderStudioFormContextAddress(
  document: SiteBuilderContentJson,
  selectedAddress: BuilderBlockAddress | null,
): BuilderBlockAddress | null {
  if (!selectedAddress) {
    return null;
  }

  for (let depth = selectedAddress.length; depth >= 1; depth -= 1) {
    const candidateAddress = selectedAddress.slice(0, depth);

    if (builderBlockAtAddress(document, candidateAddress)?.type === "form") {
      return candidateAddress;
    }
  }

  const selectedBlock = builderBlockAtAddress(document, selectedAddress);
  const wrappedContent = selectedBlock?.slots?.content;

  if (
    selectedBlock?.type !== "column"
    || selectedBlock.options?.builderTextColumn !== true
    || wrappedContent?.length !== 1
    || wrappedContent[0]?.type !== "form"
  ) {
    return null;
  }

  return [
    ...selectedAddress,
    {
      index: 0,
      key: "content",
      kind: "slot",
    },
  ];
}

/**
 * A Form wrapped for spatial placement owns its settings and nested controls,
 * while the generated Column remains the unit moved around the canvas.
 */
export function builderStudioFormMovementAddress(
  document: SiteBuilderContentJson,
  selectedAddress: BuilderBlockAddress | null,
): BuilderBlockAddress | null {
  if (!selectedAddress) {
    return null;
  }

  const selectedBlock = builderBlockAtAddress(document, selectedAddress);

  if (selectedBlock?.type !== "form" || selectedAddress.length < 2) {
    return selectedBlock ? selectedAddress : null;
  }

  const parentAddress = selectedAddress.slice(0, -1);
  const parent = builderBlockAtAddress(document, parentAddress);

  return (
    parent?.type === "column"
    && parent.options?.builderTextColumn === true
    && parent.slots?.content?.length === 1
    && parent.slots.content[0] === selectedBlock
  ) ? parentAddress : selectedAddress;
}

/**
 * Keeps the generated Column as the canvas placement unit while opening the
 * authored Form itself after an insertion. Other text-column insertions keep
 * their existing wrapper selection so inline focus and movement stay intact.
 */
export function builderStudioInsertionSelectionAddress(
  document: SiteBuilderContentJson,
  insertedAddress: BuilderBlockAddress | null,
  insertionBlock: SiteBuilderBlock,
): BuilderBlockAddress | null {
  if (!insertedAddress) {
    return null;
  }

  const insertedBlock = builderBlockAtAddress(document, insertedAddress);

  if (!insertedBlock || insertedBlock.type === "form") {
    return insertedBlock ? insertedAddress : null;
  }

  const insertedForm = insertionBlock.type === "form"
    ? insertionBlock
    : insertionBlock.type === "column"
      && insertionBlock.options?.builderTextColumn === true
      && insertionBlock.slots?.content?.length === 1
      && insertionBlock.slots.content[0]?.type === "form"
      ? insertionBlock.slots.content[0]
      : null;

  if (!insertedForm) {
    return insertedAddress;
  }

  const insertedUuid = insertedBlock.uuid?.trim();
  const insertedEntry = builderBlockEntries(document).find((entry) => (
    entry.block === insertedBlock
    || (insertedUuid && entry.block.uuid === insertedUuid)
  ));

  if (!insertedEntry) {
    return insertedAddress;
  }

  const formUuid = insertedForm.uuid?.trim();
  const nestedForm = builderBlockEntries(document).find((entry) => (
    entry.block.type === "form"
    && entry.path.startsWith(`${insertedEntry.path}/`)
    && (!formUuid || entry.block.uuid === formUuid)
  ));

  return nestedForm?.address ?? insertedAddress;
}
